import {
  EntraMFAHandlerEvidence,
  ExemptionInfo,
  MfaExemptionReason,
  PrunedConfigItem,
  UserMFAInfo,
  UserMfaStatus,
} from 'components/Integrations/Groups/MS_MFA/IntegrationConfig_MS_MFA';
import {
  SslLabsEndpointEvidence,
  SslLabsEvidence,
  SslLabsSiteEvidence,
} from 'components/Integrations/Groups/SslLabs/IntegrationConfig_SslLabs';
import { IIntegrationEvidence } from './integrationEvidence';

export const parse_Evidence_MS_MFA = (raw: string | undefined): IIntegrationEvidence | undefined => {
  if (!raw) return undefined;

  const rawEvidence = JSON.parse(raw);
  if (!rawEvidence) return undefined;

  const evidence = new EntraMFAHandlerEvidence();

  evidence.isCapped = rawEvidence.isCapped ?? false;
  evidence.evaluatedAtUtc = rawEvidence.evaluatedAtUtc ? new Date(rawEvidence.evaluatedAtUtc) : new Date();
  evidence.totalAccountsExempted = rawEvidence.totalAccountsExempted ?? 0;
  evidence.totalUserCount = rawEvidence.totalUserCount ?? 0;
  evidence.totalFindingsAfterExemptions = rawEvidence.totalFindingsAfterExemptions ?? 0;
  evidence.totalReportUsersWithoutMfa = rawEvidence.totalReportUsersWithoutMfa ?? 0;

  evidence.prunedConfig =
    rawEvidence.prunedConfig?.map((p: PrunedConfigItem) => {
      const item = {
        id: p.id,
        kind: p.kind,
        reason: p.reason,
        message: p.message,
      };

      return item;
    }) ?? [];

  evidence.findings =
    rawEvidence.findings?.map((u: UserMfaStatus) => {
      return parseUserMFAStatus(u);
    }) ?? [];

  evidence.exemptions =
    rawEvidence.exemptions?.map((u: UserMfaStatus) => {
      return parseUserMFAStatus(u);
    }) ?? [];

  return evidence;
};

const parseUserMFAStatus = (u: UserMfaStatus): UserMfaStatus => {
  const stat = new UserMfaStatus();
  stat.user = new UserMFAInfo();
  stat.exemption = new ExemptionInfo();

  if (!u) return stat;

  stat.user.id = u.user?.id ?? '';
  stat.user.name = u.user?.name ?? '';
  stat.user.email = u.user?.email ?? '';
  stat.user.type = u.user?.type ?? '';
  stat.user.isAdmin = u.user?.isAdmin ?? false;
  stat.user.methodsRegistered = u.user?.methodsRegistered ?? [];

  stat.exemption.isExempt = u.exemption?.isExempt ?? false;
  stat.exemption.reason = u.exemption?.reason ?? MfaExemptionReason.None;
  stat.exemption.userId = u.exemption?.userId ?? '';
  stat.exemption.groupId = u.exemption?.groupId ?? '';
  stat.exemption.groupName = u.exemption?.groupName ?? '';
  stat.exemption.policyId = u.exemption?.policyId ?? '';
  stat.exemption.policyName = u.exemption?.policyName ?? '';

  return stat;
};

export const parse_Evidence_SslLabs = (raw: string | undefined): IIntegrationEvidence | undefined => {
  if (!raw) return undefined;

  const rawEvidence = JSON.parse(raw);
  if (!rawEvidence) return undefined;

  const evidence = new SslLabsEvidence();
  evidence.isCapped = rawEvidence.isCapped ?? false;
  evidence.evaluatedAtUtc = rawEvidence.evaluatedAtUtc ? new Date(rawEvidence.evaluatedAtUtc) : new Date();
  evidence.minimumAcceptedGrade = rawEvidence.minimumAcceptedGrade ?? 'A';
  evidence.resultAggregation = rawEvidence.resultAggregation ?? 'worst-score';
  evidence.resultScore = rawEvidence.resultScore ?? 0;
  evidence.passed = rawEvidence.passed ?? false;

  evidence.sites =
    rawEvidence.sites?.map((site: SslLabsSiteEvidence) => {
      const parsedSite = new SslLabsSiteEvidence();
      parsedSite.host = site.host ?? '';
      parsedSite.displayName = site.displayName ?? '';
      parsedSite.assessmentStatus = site.assessmentStatus ?? '';
      parsedSite.testTimeUtc = site.testTimeUtc ? new Date(site.testTimeUtc) : undefined;
      parsedSite.worstGrade = site.worstGrade ?? '';
      parsedSite.score = site.score ?? 0;
      parsedSite.passed = site.passed ?? false;
      parsedSite.endpoints =
        site.endpoints?.map((endpoint: SslLabsEndpointEvidence) => {
          const parsedEndpoint = new SslLabsEndpointEvidence();
          parsedEndpoint.ipAddress = endpoint.ipAddress ?? '';
          parsedEndpoint.serverName = endpoint.serverName ?? '';
          parsedEndpoint.grade = endpoint.grade ?? '';
          parsedEndpoint.gradeTrustIgnored = endpoint.gradeTrustIgnored ?? '';
          parsedEndpoint.futureGrade = endpoint.futureGrade ?? '';
          parsedEndpoint.hasWarnings = endpoint.hasWarnings ?? false;
          parsedEndpoint.isExceptional = endpoint.isExceptional ?? false;
          parsedEndpoint.certificateNotAfterUtc = endpoint.certificateNotAfterUtc
            ? new Date(endpoint.certificateNotAfterUtc)
            : undefined;
          parsedEndpoint.certificateIssuer = endpoint.certificateIssuer ?? '';
          parsedEndpoint.certificateSubject = endpoint.certificateSubject ?? '';
          parsedEndpoint.protocols = endpoint.protocols ?? [];

          return parsedEndpoint;
        }) ?? [];

      return parsedSite;
    }) ?? [];

  return evidence;
};
